import clsx from 'clsx';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { Button, Form, FormControlProps, Icon, InputGroup } from '../index';
import './style.scss';

export type NumberInputProps = Pick<
  FormControlProps,
  'defaultValue' | 'id' | 'disabled' | 'isInvalid' | 'onChange' | 'onBlur'
> & {
  name: string;
  label?: React.ReactNode;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  min?: number;
  value?: number;
  withKnobs?: boolean;
  max?: number;
  onChangeValue?: (value?: number | null) => void;
  theme?: 'warning';
  size?: 'sm' | 'lg';
  renderNumber?: (number?: number) => React.ReactNode;
  sign?: string;
  tooltips?: React.ReactNode;
};

const NumberInput = ({
  label,
  helperText,
  onChangeValue,
  theme,
  withKnobs = true,
  value,
  sign,
  isInvalid,
  ...props
}: NumberInputProps) => {
  const watchedNumber = isNil(value) ? 0 : value;
  const canDecrease =
    !props.disabled && !isNil(props.min) ? watchedNumber > props.min : false;
  const canIncrease =
    !props.disabled && !isNil(props.max) ? watchedNumber < props.max : false;

  return (
    <InputGroup
      className={clsx('number-field-container', { 'with-knobs': withKnobs })}
    >
      {withKnobs && (
        <Button
          id={`decrease-${props.id || props.name}`}
          variant={theme || 'link'}
          size={props.size}
          disabled={!canDecrease}
          role={canDecrease ? 'button' : undefined}
          onClick={
            canDecrease
              ? () => {
                  const nextValue = watchedNumber - 1;
                  onChangeValue?.(nextValue);
                }
              : undefined
          }
        >
          <Icon icon='minus' size='xs' />
        </Button>
      )}

      {props.disabled && props.renderNumber ? (
        <>
          <>{props.renderNumber(watchedNumber)}</>
          <Form.Control
            {...props}
            id={props.id || props.name}
            type='hidden'
            size={props.size}
            data-cy={props.name}
          />
        </>
      ) : (
        <div
          className={clsx('input-container', {
            'with-sign': !!sign,
          })}
        >
          <Form.Control
            {...props}
            value={value}
            className={clsx('text-center pe-1 m-0 number-field fw-bold', {
              'bg-warning text-warning-text': theme === 'warning',
            })}
            id={props.id || props.name}
            type='number'
            size={props.size}
            data-cy={props.name}
            isInvalid={isInvalid}
          />
          {sign && !isInvalid && <span className='sign'>{sign}</span>}
        </div>
      )}
      {withKnobs && (
        <Button
          id={`increase-${props.id || props.name}`}
          disabled={!canIncrease}
          role={canIncrease ? 'button' : undefined}
          variant={theme || 'link'}
          size={props.size}
          onClick={
            canIncrease
              ? () => {
                  const nextValue = watchedNumber + 1;
                  onChangeValue?.(nextValue);
                }
              : undefined
          }
        >
          <Icon icon='plus' size='xs' />
        </Button>
      )}
    </InputGroup>
  );
};

export default NumberInput;
